//? This file contains the CSVTeacherManagementView component, which is responsible for rendering the modal that allows the user to import a CSV file with teachers data.
import React, { useState, useEffect, useCallback } from "react";

//* Import the Modal, Button, Form, Alert and Spinner components from react-bootstrap
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";

//* Import the useTeachers hook
import { useTeachers } from "../../../hooks/teachers/useTeachers";

//* Import the useCSVTeacherData hook
import {
  useCSVTeacherData,
  CSVTeacher,
} from "../../../hooks/CSV/teachers/useCSVTeacherData";

//* Import the useImportHistory hook
import { useImportHistory } from "../../../hooks/CSV/teachers/useImportHistoryTeachers";

//* Import the importHistoryTeacherAPI from the importHistoryTeacherAPI file
import { importHistoryTeacherAPI } from "../../../hooks/importHistory/importHistoryTeacherAPI";

//! Import the ImportHistoryTable component
import { ImportHistoryTable } from "../list/CSV/ImportHistoryTable";

//! Import the CSVTeacherPreviewTable component
import { CSVTeacherPreviewTable } from "../list/CSV/CSVTeacherPreviewTable";

//! Import the InvalidDataTable component
import { InvalidDataTable } from "../list/CSV/InvalidDataTable";

//! Import the ErrorDetailsModal component
import ErrorDetailsModal from "../list/CSV/ErrorDetailsModal";

//? Import the CSVTeacherProcessingError type from the CSVTeacher-validator file
import { CSVTeacherProcessingError } from "../../../utils/csv/csv-validatorTeacher";

type CSVTeacherManagementViewProps = {
  token: string;
  onClose: () => void;
  refetch: () => void;
};

type ErrorDetail = {
  teacher: CSVTeacher;
  error: string;
  is_restore?: boolean;
  restored?: boolean;
  id: string;
};

export function CSVTeacherManagementView({
  token,
  onClose,
  refetch,
}: CSVTeacherManagementViewProps) {
  const {
    selectedFile,
    setSelectedFile,
    CSVTeacherError,
    CSVTeacherPreviewData,
    invalidData,
    fileInputKey,
    setFileInputKey,
    validateTeacher,
    handleCSVTeacherFileUpload,
    setCSVTeacherPreviewData,
    setInvalidData,
  } = useCSVTeacherData();

  const {
    importHistory,
    isFetchingHistory,
    fetchImportHistory,
    setImportHistory,
  } = useImportHistory(token);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<CSVTeacher | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetail[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  const {
    processCSVTeacher,
    isProcessingCSVTeacher,
    CSVTeacherError: processCSVTeacherError,
  } = useTeachers(token);

  useEffect(() => {
    fetchImportHistory();
  }, [fetchImportHistory]);

  const handleSaveImportError = useCallback(async () => {
    const errorImportStatusData = {
      file_name: selectedFile?.name || "Desconhecido",
      timestamp: new Date().toISOString(),
      status: "finished" as const,
      progress: 0,
      table_name: "teachers",
      total: CSVTeacherPreviewData.length,
      success: 0,
      detailed_errors: [] as {
        id: string;
        teacher: CSVTeacher;
        error: string;
        is_restore?: boolean;
        restored?: boolean;
      }[],
      error: "Erro no processamento",
    };

    try {
      const errorImportStatus = await importHistoryTeacherAPI.create(
        errorImportStatusData,
        token
      );
      setImportHistory((prev: any) => [errorImportStatus, ...prev]);
    } catch (saveError) {
      console.error(
        "Erro ao salvar histórico de importação com falha:",
        saveError
      );
    }
  }, [selectedFile, CSVTeacherPreviewData.length, token, setImportHistory]);

  const handleProcessImportInBackground = useCallback(async () => {
    if (!selectedFile || CSVTeacherPreviewData.length === 0) return;

    try {
      const result = await processCSVTeacher(CSVTeacherPreviewData);

      const newImportStatusData = {
        file_name: selectedFile.name,
        timestamp: new Date().toISOString(),
        status: "finished" as const,
        progress: result.success,
        total: CSVTeacherPreviewData.length,
        table_name: "teachers",
        success: result.success,
        detailed_errors: result.errors.map(
          (error: CSVTeacherProcessingError) => ({
            id: error.id,
            teacher: error.teacher,
            error: error.error,
            is_restore: error.teacher.is_restore,
            restored: false,
          })
        ),
        error:
          result.errors.length > 0
            ? `${result.errors.length} erro(s) ao importar`
            : undefined,
      };

      const newImportStatus = await importHistoryTeacherAPI.create(
        newImportStatusData,
        token
      );
      setImportHistory((prev: any) => [newImportStatus, ...prev]);

      setSelectedFile(null);
      setCSVTeacherPreviewData([]);
      setInvalidData([]);
      setFileInputKey(Date.now());


      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        onClose(); 
        refetch(); 
      }, 3500);
    } catch (error) {
      console.error("Erro ao processar importação:", error);
      setProcessError("Erro ao processar importação.");
      handleSaveImportError();
    }
  }, [
    selectedFile,
    CSVTeacherPreviewData,
    processCSVTeacher,
    token,
    refetch,
    handleSaveImportError,
    setImportHistory,
    setSelectedFile,
    setCSVTeacherPreviewData,
    setInvalidData,
    setFileInputKey,
    onClose,
  ]);

  const handleTeacherRestored = useCallback((id: string) => {
    setErrorDetails((prevErrorDetails) =>
      prevErrorDetails.map((errorDetail) =>
        errorDetail.id === id
          ? { ...errorDetail, restored: true, is_restore: false }
          : errorDetail
      )
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, []);

  const handleRemovePreviewItem = useCallback(
    (index: number) => {
      setCSVTeacherPreviewData((prev: any) =>
        prev.filter((_: any, idx: any) => idx !== index)
      );
    },
    [setCSVTeacherPreviewData]
  );

  const handleRemoveInvalidItem = useCallback(
    (index: number) => {
      setInvalidData((prev: any) =>
        prev.filter((_: any, idx: any) => idx !== index)
      );
    },
    [setInvalidData]
  );

  const handleEditInvalidItem = useCallback(
    (index: number) => {
      setEditingIndex(index);
      setEditingTeacher({ ...invalidData[index] });
    },
    [invalidData]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingTeacher(null);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingTeacher !== null && editingIndex !== null) {
      const validatedTeacher = validateTeacher(editingTeacher);
      if (validatedTeacher.isValid) {
        setCSVTeacherPreviewData((prev: any) => [...prev, validatedTeacher]);
        setInvalidData((prev: any) =>
          prev.filter((_: any, idx: any) => idx !== editingIndex)
        );
      } else {
        setInvalidData((prev: any) =>
          prev.map((item: any, idx: any) =>
            idx === editingIndex ? validatedTeacher : item
          )
        );
      }
      setEditingIndex(null);
      setEditingTeacher(null);
    }
  }, [
    editingTeacher,
    editingIndex,
    validateTeacher,
    setCSVTeacherPreviewData,
    setInvalidData,
  ]);

  const handleChangeEditingTeacher = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setEditingTeacher((prev: any) =>
        prev ? { ...prev, [name]: value } : prev
      );
    },
    []
  );

  const openErrorModal = useCallback(
    (
      errors: {
        teacher: CSVTeacher;
        error: string;
        is_restore?: boolean;
        id: string;
      }[]
    ) => {
      setErrorDetails((prevErrorDetails) => {
        const updatedErrorDetails: ErrorDetail[] = errors.map((error) => {
          const existingError = prevErrorDetails.find(
            (ed) => ed.id === error.id
          );
          return {
            ...error,
            restored: existingError ? existingError.restored : false,
          };
        });
        return updatedErrorDetails;
      });
      setShowErrorModal(true);
    },
    []
  );

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const downloadExampleCSV = () => {
    const exampleCSV =
      "Nome,E-mail\nExemplo1,Exemplo1@example.com\nExemplo2,Exemplo2@example.com";
    const blob = new Blob([exampleCSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo-exemplo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Modal show onHide={onClose} size="xl" dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Importar Professores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImportHistoryTable
            importHistory={importHistory}
            isFetchingHistory={isFetchingHistory}
            openErrorModal={openErrorModal}
          />
          <Form.Group>
            <Form.Label>
              Arquivo .csv <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-2 align-items-center mb-2">
              <Button
                variant="outline-secondary"
                onClick={() => document.getElementById("csvFileInput")?.click()}
              >
                Escolher arquivo
              </Button>
              {selectedFile && (
                <span className="text-muted">{selectedFile.name}</span>
              )}
              <Form.Control
                type="file"
                accept=".csv"
                key={fileInputKey}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.files && input.files[0]) {
                    handleCSVTeacherFileUpload(input.files[0]);
                  }
                }}
                style={{ display: "none" }}
                id="csvFileInput"
              />
            </div>
            <Form.Text className="text-muted">
              <strong>Padrão:</strong> O arquivo .csv não deve ter cabeçalho
              (nome das colunas). As colunas devem ser separadas por "," no
              seguinte formato:
              <br />
              Nome, E-mail
              <br />
              <Button variant="link" onClick={downloadExampleCSV}>
                Baixar modelo de exemplo
              </Button>
            </Form.Text>
          </Form.Group>

          {CSVTeacherError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erros encontrados:</strong>
              <pre className="mb-0 mt-2 small">{CSVTeacherError}</pre>
            </Alert>
          )}

          {processCSVTeacherError && (
            <Alert variant="danger" className="mt-3">
              <strong>Erro no processamento:</strong>
              <pre className="mb-0 mt-2 small">
                {processCSVTeacherError.message}
              </pre>
            </Alert>
          )}

          <Form.Group className="mt-4">
            <Form.Label>Pesquisar professores</Form.Label>
            <Form.Control
              type="text"
              placeholder="Pesquisar por nome ou email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>

          <CSVTeacherPreviewTable
            data={CSVTeacherPreviewData}
            searchTerm={searchTerm}
            handleRemovePreviewItem={handleRemovePreviewItem}
          />

          <InvalidDataTable
            data={invalidData}
            searchTerm={searchTerm}
            editingIndex={editingIndex}
            editingTeacher={editingTeacher}
            handleEditInvalidItem={handleEditInvalidItem}
            handleRemoveInvalidItem={handleRemoveInvalidItem}
            handleChangeEditingTeacher={handleChangeEditingTeacher}
            handleSaveEdit={handleSaveEdit}
            handleCancelEdit={handleCancelEdit}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleProcessImportInBackground}
            disabled={
              CSVTeacherPreviewData.length === 0 || isProcessingCSVTeacher
            }
          >
            {isProcessingCSVTeacher ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                IMPORTANDO
              </>
            ) : (
              "IMPORTAR"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ErrorDetailsModal
        show={showErrorModal}
        onHide={closeErrorModal}
        errorDetails={errorDetails}
        token={token}
        onTeacherRestored={handleTeacherRestored}
      />

      {processError && (
        <Alert
          variant="danger"
          className="mt-3"
          onClose={() => setProcessError(null)}
          dismissible
        >
          {processError}
        </Alert>
      )}
    </>
  );
}

export default CSVTeacherManagementView;
